<template>
  <div
    class="banner flex items-center"
    :class="[background ? background : 'bg-var(--color-primary-500)', heroHeaderEmbed ? 'banner-video' : '']"
  >
    <div v-if="heroHeaderEmbed" class="yt-video">
      <iframe
        id=""
        title="yt-video"
        class="h-full w-full"
        type="text/html"
        width="100%"
        frameborder="0"
        :src="`https://www.youtube-nocookie.com/embed/${getEmbedCode(
          heroHeaderEmbed,
        )}?&amp;autoplay=1&amp;playsinline=1&amp;mute=1&amp;loop=1&amp;showinfo=0&amp;controls=0&amp;rel=0&amp;wmode=transparent&amp;disablekb=1&amp;playlist=${getEmbedCode(
          heroHeaderEmbed,
        )}`"
      ></iframe>
    </div>
    <div v-else-if="heroHeaderImage" class="banner-image-wrapper w-full">
      <VcImage :src="heroHeaderImage" class="banner-image" lazy />
    </div>
    <div class="banner-content absolute z-[2] w-full max-w-[600px] px-4 text-center lg:px-0">
      <div
        class="hero-header mb-8 text-4xl font-bold leading-tight lg:text-[64px]"
        :class="textColor ? textColor : 'text-white'"
        v-html="heroTitle"
      ></div>
      <div class="hero-text mb-8 text-lg" :class="textColor ? textColor : 'text-white'" v-html="heroSubtitle"></div>

      <div class="flex flex-col items-center">
        <VcIcon
          v-if="anchorLink"
          class="h-16 w-16 cursor-pointer rounded-full bg-[--color-secondary] p-5 text-white"
          name="arrow-down"
          @click="handleClick"
        />

        <VcButton v-if="showTheButton" :to="buttonLink" color="secondary" class="mt-4 w-fit px-8 md:w-1/2 lg:w-auto">
          {{ buttonLabel }}
        </VcButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

type ButtonType = {
  label: string;
  link: string;
};

type ContentType = {
  html: string;
  markdown: string;
};

interface IProps {
  id: string;
  background?: string;
  textColor?: string;
  bottomMargin?: string;
  verticalPadding?: string;
  heroHeaderEmbed?: string;
  heroHeaderImage?: string;
  heroTitle?: string;
  heroSubtitle?: ContentType;
  anchorLink?: Array<ButtonType>;
  buttonLink?: string;
  buttonLabel?: string;
}

const props = defineProps<IProps>();

const handleClick = () => {
  const cardsCta = document.querySelector("#cardscalltoactionerfW");

  setTimeout(function () {
    if (cardsCta) {
      cardsCta.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, 2);
};

const showTheButton = computed(() => {
  return Boolean(props.buttonLink) && Boolean(props.buttonLabel);
});

function getEmbedCode(url: string) {
  const parts = url.split("/");
  return parts[parts.length - 1]; // Get the last part of the URL
}
</script>

<style lang="scss" scoped>
.yt-video {
  overflow: hidden;
  width: 100%; /* Keep it the right aspect-ratio */
  aspect-ratio: 16/9; /* No clicking/hover effects */
  pointer-events: none;
}

.yt-video iframe {
  width: 300%; /* Extend it beyond the viewport... */
  height: 100%;
  margin-left: -100%; /* ...and bring it back again */
}

.banner {
  position: relative;
  height: calc(75vh - 50px);
}

.banner-image,
.banner-video {
  width: 100%;
  height: calc(75vh - 50px);
  object-fit: cover;
}

.banner-video {
  height: auto;
}

.banner-content {
  left: 50%;
  top: 25%;
  transform: translate(-50%, -25%);
}

.banner-image-wrapper::after,
.yt-video::after {
  content: "";
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
  z-index: 1;
  position: absolute;
  top: 0;
  left: 0;
}

@media (min-width: 768px) {
  .banner,
  .banner-image,
  .banner-video {
    height: calc(70vh);
  }

  .banner-video {
    height: auto;
  }
}

@media (max-width: 1024px) {
  .yt-video + .banner-content.absolute {
    margin-top: -75px;
  }
}

@media (max-width: 768px) {
  .yt-video + .banner-content.absolute {
    margin-top: -35px;
    /* If the following item is a cards-cta its top margin is altered there */

    .hero-header {
      font-size: 2rem;
      line-height: 100%;
      margin-bottom: 1rem;
    }

    .hero-text {
      font-size: 1rem;
      line-height: 100%;
      margin-bottom: 1rem;
    }
  }
}

@media (max-width: 500px) {
  .yt-video + .banner-content.absolute {
    margin-top: 0;
    /* If the following item is a cards-cta its top margin is altered there */

    .hero-header {
      font-size: 1.2rem;
      line-height: 100%;
      margin-bottom: 1rem;
    }

    .hero-text {
      font-size: 1rem;
      line-height: 100%;
      margin-bottom: 1rem;
    }
  }
}
</style>
